import { useMemo, useRef, useState } from "react";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    Autocomplete,
    Box,
    CircularProgress,
    Collapse,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";

import { DEV_MODE } from "Constant/app/process";
import { ButtonVariant } from "Constant/button";
import { capitalizeFirstLetter } from "Constant/string";
import useUpdateEffect from "hooks/useUpdateEffect";

import {
    authorizeTiktokCustomAudience,
    getTiktokAdvertisers,
    isAuthTikTok,
} from "helpers/destinations/createDestination";

import StandartButton from "components/atoms/StandartButton";
import LoginButton from "components/molecules/LoginButton";
import SelectOrCreateSwitch, {
    SelectOrCreateMode,
} from "components/molecules/SelectOrCreateSwitch";
import SwalAlertComp from "components/SwalAlert";

const AUDIENCE_NAME_MAX_LENGTH = 127;
const DEFAULT_RETENTION_DAYS = "365";
const DEFAULT_DATA_SENDER_METHOD = "add";
const DEV_FETCH_DELAY_MS = 700;

const DEV_MODE_MOCK_LOGIN_PARAMS = {
    auth_code: "dev-mock-tiktok-code",
    state: "dev-mock-tiktok-state",
};

const DEV_MODE_MOCK_ADVERTISERS = [
    {
        advertiser_id: "1111111111111111111",
        advertiser_name: "Dev Ad Account A",
    },
    {
        advertiser_id: "2222222222222222222",
        advertiser_name: "Dev Ad Account B",
    },
];

const DEV_MODE_MOCK_AUDIENCES_BY_ADVERTISER = {
    "1111111111111111111": [
        { audience_id: "aud-1", audience_name: "Dev Audience One" },
        { audience_id: "aud-2", audience_name: "Dev Audience Two" },
    ],
    "2222222222222222222": [
        { audience_id: "aud-3", audience_name: "Dev Audience Three" },
    ],
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatFileSize = (bytes) => {
    if (!bytes || bytes <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const exponent = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        units.length - 1,
    );
    const value = bytes / Math.pow(1024, exponent);
    return `${value.toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
};

const RETENTION_DAYS_OPTIONS = ["1", "7", "14", "30", "60", "90", "180", "365"];

const dataSenderMethodOptions = [
    {
        value: "add",
        label: "Add (Default)",
        description: "New users are added to the existing audiences.",
    },
    {
        value: "replace",
        label: "Replace",
        description:
            "The audience data is completely replaced. New data must be sent within a 90-minute session.",
    },
];

const TiktokCustomAudience = ({
    isLoadingButton,
    setTiktokCustomAudienceInfos,
    setIsSaveAvailable,
}) => {
    const [isLoading, setIsLoading] = useState(() => isLoadingButton);
    const [isSuccess, setIsSucess] = useState(false);

    // Ad accounts come from GET /tiktok/advertisers (no audiences).
    const [addAccounts, setAddAccounts] = useState(null);
    const [selectedAddAccount, setSelectedAddAccount] = useState(null);

    // Audiences are fetched per selected ad account via POST /tiktok/ca/authorize.
    const [customAudiences, setCustomAudiences] = useState(null);
    const [isAudiencesLoading, setIsAudiencesLoading] = useState(false);
    const audienceRequestRef = useRef(0);

    const [audienceMode, setAudienceMode] = useState(
        SelectOrCreateMode.EXISTING,
    );
    const [selectedCustomAudience, setSelectedCustomAudience] = useState(null);

    // Create new audience fields
    const [newAudienceName, setNewAudienceName] = useState("");
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    const [retentionDays, setRetentionDays] = useState(DEFAULT_RETENTION_DAYS);
    const [dataSenderMethod, setDataSenderMethod] = useState(
        DEFAULT_DATA_SENDER_METHOD,
    );

    const isCreatingNew = audienceMode === SelectOrCreateMode.CREATE;

    const resetAudienceFields = () => {
        setSelectedCustomAudience(null);
        setNewAudienceName("");
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const loadAdvertisers = async ({ code = null, state = null } = {}) => {
        setIsLoading(true);
        try {
            if (DEV_MODE) {
                await wait(DEV_FETCH_DELAY_MS);
                setAddAccounts(DEV_MODE_MOCK_ADVERTISERS);
                setIsSucess(true);
                return;
            }

            const response = await getTiktokAdvertisers(
                code && state ? { code, state } : {},
            );

            if (response?.status === false || !response?.data?.length) {
                setAddAccounts(null);
                setIsSucess(false);
                SwalAlertComp({
                    title: "Failed",
                    message:
                        response?.message ||
                        "Could not load TikTok ad accounts. Please try again.",
                    icon: "error",
                });
                return;
            }

            setAddAccounts(response.data);
            setIsSucess(true);
        } catch (error) {
            setAddAccounts(null);
            setIsSucess(false);
            SwalAlertComp({
                title: "Failed",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "An error occurred",
                icon: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const loadCustomAudiences = async (advertiserId) => {
        const requestId = ++audienceRequestRef.current;
        setCustomAudiences(null);

        if (!advertiserId) {
            setIsAudiencesLoading(false);
            return;
        }

        setIsAudiencesLoading(true);
        try {
            if (DEV_MODE) {
                await wait(DEV_FETCH_DELAY_MS);
                if (requestId !== audienceRequestRef.current) return;
                setCustomAudiences(
                    DEV_MODE_MOCK_AUDIENCES_BY_ADVERTISER[advertiserId] ?? [],
                );
                return;
            }

            // The request helper surfaces the alert on failure.
            const response = await authorizeTiktokCustomAudience({
                account_id: advertiserId,
            });

            // Ignore responses for an account the user already moved away from.
            if (requestId !== audienceRequestRef.current) return;

            if (response?.status === false) {
                // Token may need re-auth (backend soft-deletes it on 401);
                // show the TikTok login button again so the user can reconnect.
                setIsSucess(false);
                setSelectedAddAccount(null);
                return;
            }

            setCustomAudiences(response?.data?.custom_audiences ?? []);
        } finally {
            if (requestId === audienceRequestRef.current) {
                setIsAudiencesLoading(false);
            }
        }
    };

    const getAuthUrl = async () => {
        const isAuth = await isAuthTikTok();

        if (isAuth?.status === false) {
            SwalAlertComp({
                title: "Failed",
                message:
                    isAuth?.message ||
                    "Could not start TikTok authorization. Please try again.",
                icon: "error",
            });
            return null;
        }

        if (isAuth?.data?.authenticated) {
            await loadAdvertisers();
            return null;
        }
        return isAuth?.data?.auth_url ?? null;
    };

    const onLoginSuccess = (data) => {
        setAddAccounts(null);
        setSelectedAddAccount(null);
        loadAdvertisers({ code: data.code, state: data.state });
    };

    const handleModeChange = (mode) => {
        if (mode === audienceMode) return;
        resetAudienceFields();
        setAudienceMode(mode);
    };

    const selectedAudienceName = useMemo(
        () =>
            customAudiences?.find(
                ({ audience_id }) => selectedCustomAudience === audience_id,
            )?.audience_name,
        [customAudiences, selectedCustomAudience],
    );

    useUpdateEffect(() => {
        const trimmedName = newAudienceName.trim();
        const hasSharedFields = Boolean(
            selectedAddAccount && retentionDays && dataSenderMethod,
        );

        if (isCreatingNew) {
            setTiktokCustomAudienceInfos({
                advertiser_id: selectedAddAccount,
                custom_audience_id: null,
                custom_audience_name: trimmedName,
                retention_days: retentionDays,
                data_sender_method: dataSenderMethod,
                file,
                isCreatingNew: true,
            });
            setIsSaveAvailable(
                hasSharedFields &&
                    trimmedName.length > 0 &&
                    trimmedName.length <= AUDIENCE_NAME_MAX_LENGTH &&
                    file !== null,
            );
            return;
        }

        setTiktokCustomAudienceInfos({
            advertiser_id: selectedAddAccount,
            custom_audience_id: selectedCustomAudience,
            custom_audience_name: selectedAudienceName,
            retention_days: retentionDays,
            data_sender_method: dataSenderMethod,
            isCreatingNew: false,
        });
        setIsSaveAvailable(hasSharedFields && Boolean(selectedCustomAudience));
    }, [
        isCreatingNew,
        selectedCustomAudience,
        selectedAudienceName,
        selectedAddAccount,
        newAudienceName,
        retentionDays,
        dataSenderMethod,
        file,
    ]);

    // Ad account changed: clear previous selection and fetch only that account's audiences.
    useUpdateEffect(() => {
        resetAudienceFields();
        setAudienceMode(SelectOrCreateMode.EXISTING);
        setRetentionDays(DEFAULT_RETENTION_DAYS);
        setDataSenderMethod(DEFAULT_DATA_SENDER_METHOD);
        loadCustomAudiences(selectedAddAccount);
    }, [selectedAddAccount]);

    const sharedFieldsDisabled = !selectedAddAccount;
    const hasNoAudiences =
        !isAudiencesLoading &&
        Array.isArray(customAudiences) &&
        !customAudiences.length;

    return (
        <Box>
            <div className="position-relative overflow-hidden z-1 mt-3">
                {!isSuccess && (
                    <LoginButton
                        code="tiktok"
                        getAuthUrl={getAuthUrl}
                        paramNames={["auth_code", "state"]}
                        onSuccess={onLoginSuccess}
                        devSuccessParams={DEV_MODE_MOCK_LOGIN_PARAMS}
                        dataTransform={(params) => ({
                            code: params.auth_code,
                            state: params.state,
                        })}
                        logoSrc="/assets/images/typeImages/tiktok-icon.png"
                        buttonText="Create with Tiktok Custom Audience"
                        isLoadingExternal={isLoading}
                    />
                )}
            </div>

            <Collapse in={isSuccess} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flow-root" }}>
                    {/* Ad Account Select */}
                    <FormControl fullWidth className="mt-3">
                        <Autocomplete
                            options={addAccounts ?? []}
                            value={
                                addAccounts?.find(
                                    ({ advertiser_id }) =>
                                        advertiser_id === selectedAddAccount,
                                ) ?? null
                            }
                            onChange={(_event, selected) =>
                                setSelectedAddAccount(
                                    selected?.advertiser_id ?? null,
                                )
                            }
                            disabled={!addAccounts?.length}
                            className={
                                !addAccounts?.length ? "disabled-inputs" : ""
                            }
                            getOptionLabel={(option) =>
                                option?.advertiser_name ?? ""
                            }
                            isOptionEqualToValue={(option, value) =>
                                option?.advertiser_id === value?.advertiser_id
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Ad Account"
                                    required
                                />
                            )}
                        />
                    </FormControl>
                </Box>
            </Collapse>

            <Collapse
                in={Boolean(selectedAddAccount)}
                timeout="auto"
                unmountOnExit
            >
                <Box sx={{ display: "flow-root" }}>
                    {/* Custom Audience: Select Existing / Create New */}
                    <SelectOrCreateSwitch
                        label="Custom Audience"
                        required
                        mode={audienceMode}
                        onModeChange={handleModeChange}
                        disabled={!selectedAddAccount}
                    >
                        {isAudiencesLoading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    py: 2,
                                }}
                            >
                                <CircularProgress size={20} />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Loading audiences...
                                </Typography>
                            </Box>
                        ) : isCreatingNew ? (
                            <Box>
                                <FormControl fullWidth>
                                    <TextField
                                        name="custom_audience_name"
                                        label="Audience Name"
                                        required
                                        variant="outlined"
                                        size="small"
                                        onChange={(e) =>
                                            setNewAudienceName(e.target.value)
                                        }
                                        value={newAudienceName}
                                        fullWidth
                                        slotProps={{
                                            htmlInput: {
                                                maxLength:
                                                    AUDIENCE_NAME_MAX_LENGTH,
                                            },
                                        }}
                                    />
                                </FormControl>

                                <FormControl fullWidth className="mt-3">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv,.txt"
                                        style={{ display: "none" }}
                                        onChange={(e) =>
                                            setFile(e.target.files?.[0] ?? null)
                                        }
                                    />
                                    <StandartButton
                                        variant={ButtonVariant.BLACK_OUTLINED}
                                        fullWidth
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                        sx={{
                                            justifyContent: "flex-start",
                                            py: 1.5,
                                            gap: 1,
                                        }}
                                    >
                                        <UploadFileIcon fontSize="small" />
                                        {file
                                            ? file.name
                                            : "Upload Audience File (.csv, .txt, max 100MB) *"}
                                    </StandartButton>
                                </FormControl>
                            </Box>
                        ) : (
                            <FormControl fullWidth>
                                <Select
                                    label="Custom Audience"
                                    required
                                    size="small"
                                    displayEmpty
                                    value={selectedCustomAudience ?? ""}
                                    onChange={({ target: { value } }) =>
                                        setSelectedCustomAudience(value)
                                    }
                                    disabled={
                                        !selectedAddAccount || hasNoAudiences
                                    }
                                    className={
                                        !selectedAddAccount
                                            ? "disabled-inputs"
                                            : ""
                                    }
                                    renderValue={(value) => {
                                        if (hasNoAudiences)
                                            return "No custom audiences in this ad account";

                                        return (
                                            customAudiences?.find(
                                                ({ audience_id }) =>
                                                    audience_id === value,
                                            )?.audience_name ?? ""
                                        );
                                    }}
                                >
                                    {customAudiences?.map(
                                        ({ audience_id, audience_name }) => (
                                            <MenuItem
                                                value={audience_id}
                                                key={audience_id}
                                            >
                                                {audience_name}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                                {hasNoAudiences && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        This ad account has no custom audiences
                                        yet. Use Create New to add one.
                                    </Typography>
                                )}
                            </FormControl>
                        )}
                    </SelectOrCreateSwitch>

                    {/* Retention Days — both create and store */}
                    <FormControl fullWidth className="mt-3">
                        <Select
                            label="Retention Days"
                            required
                            value={retentionDays}
                            onChange={({ target: { value } }) =>
                                setRetentionDays(value)
                            }
                            disabled={sharedFieldsDisabled}
                            className={
                                sharedFieldsDisabled ? "disabled-inputs" : ""
                            }
                        >
                            {RETENTION_DAYS_OPTIONS.map((value) => (
                                <MenuItem value={value} key={value}>
                                    {value === DEFAULT_RETENTION_DAYS
                                        ? "365 Days (Default)"
                                        : `${capitalizeFirstLetter(value)} Days`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Data Sender Method — both create and store */}
                    <FormControl fullWidth className="mt-3">
                        <Select
                            value={dataSenderMethod}
                            onChange={(event) => {
                                setDataSenderMethod(event.target.value);
                            }}
                            fullWidth
                            label="Data Sender Method"
                            size="small"
                            disabled={sharedFieldsDisabled}
                            className={
                                sharedFieldsDisabled ? "disabled-inputs" : ""
                            }
                            aria-label="data-sender-method"
                        >
                            {dataSenderMethodOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    <div>
                                        <Typography fontWeight={500}>
                                            {option.label}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {option.description}
                                        </Typography>
                                    </div>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Collapse>
        </Box>
    );
};

export default TiktokCustomAudience;
